import { addItemToLS, getItemFromLS } from 'utils/localStorage'

export const keyOf = (col, value) => `${col}::${value}`
export const splitKey = (k) => {
  if (typeof k !== 'string') return ['', '']
  return k.split('::')
}

export const getDistinctColumnValues = ({ pinOptions, data }) => {
  if (!data) return {}
  const distinctValuesMap = {}

  pinOptions.forEach((column) => {
    if (column.external) {
      distinctValuesMap[column.key] = { label: column.label, values: new Set(column.options), external: true }
      return
    }

    distinctValuesMap[column.key] = { label: column.label, values: new Set() }
    data.forEach((item) => {
      if (item[column.key]) distinctValuesMap[column.key].values.add(item[column.key])
    })
  })

  return Object.fromEntries(
    Object.entries(distinctValuesMap).map(([key, value]) => [
      key,
      { label: value.label, values: Array.from(value.values), external: value.external },
    ]),
  )
}

export const modelToActivePinned = (model, pinnedFilters) => {
  const next = {}
  if (!model) return next

  for (const [col, def] of Object.entries(model)) {
    const pinnedForCol = pinnedFilters?.filter((f) => f.column === col) || []
    if (pinnedForCol.length === 0) continue

    for (const pinned of pinnedForCol) {
      if (def?.filterType === 'set' && Array.isArray(def.values) && def.values.includes(pinned.value)) {
        next[keyOf(col, pinned.value)] = true
      }
    }
  }

  return next
}

export const getPinnedSelectionsByCol = ({ pinOptions, activePinned, excludeExternal = false }) => {
  if (!Array.isArray(pinOptions)) return {}

  const colsInUse = excludeExternal
    ? pinOptions.filter((c) => !c.external).map((c) => c.key)
    : pinOptions.map((c) => c.key)
  if (colsInUse.length === 0) return {}

  const activePinnedByCol = {}
  for (const k of Object.keys(activePinned || {})) {
    const [colKey, value] = splitKey(k)
    if (!colKey) continue
    if (!activePinnedByCol[colKey]) activePinnedByCol[colKey] = new Set()
    activePinnedByCol[colKey].add(String(value))
  }

  const byCol = {}
  for (let i = 0; i < colsInUse.length; i++) {
    const col = colsInUse[i]
    byCol[col] = activePinnedByCol[col] || new Set()
  }

  return byCol
}

export const runFilterModelOnGrid = ({ api, activePinned, pinnedFilters, storageKey }) => {
  if (!api || api.isDestroyed?.()) return

  const currentModel = api.getFilterModel() || {}
  const nextModel = { ...currentModel }

  const pinnedByCol = {}
  for (const { column, value } of pinnedFilters || []) {
    if (!pinnedByCol[column]) pinnedByCol[column] = new Set()
    pinnedByCol[column].add(String(value))
  }

  for (const [col, pinnedValues] of Object.entries(pinnedByCol)) {
    const existingValues = new Set(currentModel[col]?.filterType === 'set' ? currentModel[col].values : [])

    for (const v of pinnedValues) {
      if (activePinned[keyOf(col, v)]) {
        existingValues.add(v)
        continue
      }

      existingValues.delete(v)
    }

    if (existingValues.size === 0) {
      delete nextModel[col]
      continue
    }
    nextModel[col] = { filterType: 'set', values: Array.from(existingValues) }
  }

  api.setFilterModel(nextModel)
  addItemToLS(storageKey, 'filters', nextModel)
}

export function filterItems(itemsObj, search, pinnedSet) {
  if (!itemsObj || typeof itemsObj !== 'object') return []

  const trimmedSearch = (search || '').trim().toLowerCase()
  const allFilters = []
  const pinnedOut = []

  for (const column of Object.keys(itemsObj)) {
    let values = itemsObj[column].values ?? []
    let pinnedValues = []
    for (const value of values) {
      if (pinnedSet.has(keyOf(column, value))) {
        pinnedValues.push(value)
      }
    }

    if (!Array.isArray(values)) continue

    if (trimmedSearch) {
      const columnMatches = column.toLowerCase().includes(trimmedSearch)
      values = values.filter((value) => columnMatches || String(value).toLowerCase().includes(trimmedSearch))
      pinnedValues = values.filter((value) => pinnedSet.has(keyOf(column, value)))
    }

    if (values.length > 0) {
      allFilters.push({ column, label: itemsObj[column].label, values, external: itemsObj[column].external })
    }

    if (pinnedValues.length > 0) {
      pinnedOut.push({
        column,
        label: itemsObj[column].label,
        values: pinnedValues,
        external: itemsObj[column].external,
      })
    }
  }

  return { allFilters, pinned: pinnedOut }
}

export const EXPANDED_LS_KEY = 'pinTree.expanded.v1'
export function loadExpandedStateFromLS() {
  try {
    const raw = getItemFromLS('wellListPinPopup', EXPANDED_LS_KEY)
    return new Set(Array.isArray(raw) ? raw : [])
  } catch {
    return new Set()
  }
}
